import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  forwardRef,
  useImperativeHandle,
} from 'react';
import clsx from 'clsx';
import Konva from 'konva';
import { Stage, Layer, Line, Path, StageProps, Rect } from 'react-konva';
import { Vector2d } from 'konva/lib/types';
import { Points, Lines } from '@/types';
import { convertPointsToOutputPoints } from '@/utils/vectors';

const DEFAULT_ANIMATION_TIME = 3000;

interface AnimationProps {
  imagePath: string;
  pathPoints: Points;
  time?: number;
  loop?: boolean;
}

export interface Animation {
  play: () => void;
}

export const Animation = forwardRef<Animation, AnimationProps>(
  (
    { imagePath, pathPoints, time = DEFAULT_ANIMATION_TIME, loop = false },
    ref,
  ) => {
    const pathRef = useRef<Konva.Path | null>(null);
    const [isPlaying, setIsPlaying] = useState<boolean>(false);

    const handleAnimation = useCallback(
      (anim: Konva.Animation, start: number, end: boolean) => {
        if (end && !loop) {
          anim.stop();
          setIsPlaying(false);
        } else if (end && loop) {
          anim.start();
        }
      },
      [loop],
    );

    const FINGER_OFFSET = 35;

    const startAnimation = useCallback(() => {
      if (!isPlaying && pathRef.current) {
        const anim = new Konva.Animation((frame) => {
          if (!frame) {
            return;
          }

          const adjustedTime = time * (pathPoints.length / 100);
          const internalTime = frame.time % adjustedTime;
          const index = Math.floor(
            (internalTime / adjustedTime) * pathPoints.length,
          );
          const point = pathPoints[index] || pathPoints[0];

          pathRef.current?.position({
            x: point.x,
            y: point.y - FINGER_OFFSET,
          });

          if (internalTime >= adjustedTime - frame.timeDiff) {
            handleAnimation(anim, frame.time, true);
          }
        }, pathRef.current.getLayer());

        anim.start();

        setIsPlaying(true);
      }
    }, [isPlaying, handleAnimation, pathPoints, time]);

    const stopAnimation = useCallback(() => {
      if (isPlaying) {
        setIsPlaying(false);
      }
    }, [isPlaying]);

    useImperativeHandle(ref, () => ({
      play: startAnimation,
      stop: stopAnimation,
    }));

    useEffect(() => {
      return () => {
        stopAnimation();
      };
    }, [stopAnimation]);

    return (
      <Path
        ref={pathRef}
        data={imagePath}
        stroke="black"
        strokeWidth={3}
        fill="black"
        visible={isPlaying}
        scaleX={0.3}
        scaleY={0.3}
        x={100}
        y={100}
      />
    );
  },
);

Animation.displayName = 'Animation';

export interface KonvaInstanceElement extends HTMLDivElement {
  _stageRef: Konva.Stage | null;
  _linesLayerRef: Konva.Layer | null;
}

export interface LineProps {
  tool: string;
  points: number[];
  color?: string;
}

type Path = string;

export interface PathConfig {
  path: Path;
  xOffset?: number;
  yOffset?: number;
  color?: string;
  fillColor?: string;
  strokeWidth?: number;
  dashed?: boolean;
  scaleX?: number;
  scaleY?: number;
  ref?: React.MutableRefObject<Konva.Path>;
  hidden?: boolean;
}

export interface AnimationConfig {
  ref?: React.MutableRefObject<Animation | null>;
  imagePath: string;
  animationPoints: Points;
  loop?: boolean;
}

interface CanvasDrawProps {
  width?: number;
  height?: number;
  fill?: boolean;
  currentColor?: string;
  inputPaths?: Array<PathConfig>;
  animations?: Array<AnimationConfig>;
  includeBgOnLines?: boolean;
  setOutputLines: (lines: Lines) => void;
}

export interface CanvasDraw {
  reset: () => void;
  asBitmap: () => string | undefined;
}

const DEFAULT_WIDTH_HEIGHT = 384;

export const CanvasDraw = forwardRef<CanvasDraw, CanvasDrawProps>(
  (
    {
      width = DEFAULT_WIDTH_HEIGHT,
      height = DEFAULT_WIDTH_HEIGHT,
      fill = false,
      currentColor = '#000',
      inputPaths = [],
      animations = [],
      includeBgOnLines = false,
      setOutputLines,
    },
    ref,
  ) => {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const stageRef = useRef<Konva.Stage | null>(null);
    const linesLayerRef = useRef<Konva.Layer | null>(null);
    const isDrawing = useRef<boolean>(false);

    const [resolvedWidth, setResolvedWidth] = useState<number>(width);
    const [resolvedHeight, setResolvedHeight] = useState<number>(height);

    const [lines, setLines] = useState<Array<LineProps>>([]);

    useImperativeHandle(ref, () => ({
      reset: () => {
        setLines([]);
        setOutputLines([]);
      },
      asBitmap: () => linesLayerRef.current?.toDataURL(),
    }));

    const EPS = 0.001;

    const handleDrawDown = (pos?: Vector2d | null) => {
      isDrawing.current = true;

      if (pos) {
        setLines([
          ...lines,
          {
            tool: 'pen',
            points: [pos.x, pos.y, pos.x + EPS, pos.y + EPS],
            color: currentColor,
          },
        ]);
      }
    };

    const handleMouseDown: StageProps['onMouseDown'] = (e) => {
      handleDrawDown(e.target.getStage()?.getPointerPosition());
    };

    const handleTouchStart: StageProps['onTouchStart'] = (e) => {
      handleDrawDown(e.target.getStage()?.getPointerPosition());
    };

    const handleDrawMove = (pos?: Vector2d | null) => {
      if (!isDrawing.current) {
        return;
      }

      if (pos) {
        const lastLine = lines[lines.length - 1];
        lastLine.points = lastLine.points.concat([pos.x, pos.y]);

        lines.splice(lines.length - 1, 1, lastLine);
        setLines(lines.concat());
      }
    };

    const handleMouseMove: StageProps['onMouseMove'] = (e) => {
      handleDrawMove(e.target.getStage()?.getPointerPosition());
    };

    const handleTouchMove: StageProps['onTouchMove'] = (e) => {
      handleDrawMove(e.target.getStage()?.getPointerPosition());
    };

    const handleDrawUp = () => {
      isDrawing.current = false;
    };

    const handleMouseUp: StageProps['onMouseUp'] = () => {
      handleDrawUp();
    };

    const handleTouchEnd: StageProps['onTouchEnd'] = () => {
      handleDrawUp();
    };

    useEffect(() => {
      if (!fill) {
        return;
      }
      if (!containerRef.current) {
        return;
      }

      const container = containerRef.current;

      const resizeObserver = new ResizeObserver(() => {
        const { width: containerWidth, height: containerHeight } =
          container.getBoundingClientRect();

        setResolvedWidth(containerWidth);
        setResolvedHeight(containerHeight);
      });

      resizeObserver.observe(container);
    }, [fill]);

    useEffect(() => {
      if (!lines.length) {
        return;
      }

      const convertLinesToOutputLines = (lines: Array<LineProps>): Lines =>
        lines.map((line) => ({
          tool: line.tool,
          points: convertPointsToOutputPoints(line.points),
        }));

      setOutputLines(convertLinesToOutputLines(lines));
    }, [lines, setOutputLines]);

    useEffect(() => {
      if (stageRef.current) {
        const container = stageRef.current.container() as KonvaInstanceElement;
        container._stageRef = stageRef.current;
        container._linesLayerRef = linesLayerRef.current;
      }
    }, []);

    const DASHED_DASH = 1;
    const DASHED_GAP = 1;

    return (
      <div
        ref={containerRef}
        className={clsx(
          'overflow-x-auto',
          fill
            ? 'w-full h-full'
            : `w-[${resolvedWidth}px] h-[${resolvedHeight}px]`,
        )}
      >
        <Stage
          ref={stageRef}
          width={resolvedWidth}
          height={resolvedHeight}
          onMouseDown={handleMouseDown}
          onTouchStart={handleTouchStart}
          onMouseMove={handleMouseMove}
          onTouchMove={handleTouchMove}
          onMouseUp={handleMouseUp}
          onTouchEnd={handleTouchEnd}
          style={{
            touchAction: 'none',
          }}
        >
          {inputPaths?.length > 0 && (
            <Layer>
              {inputPaths.map((inputPath, i) => (
                <Path
                  key={i}
                  data={inputPath.path}
                  stroke={inputPath.color || '#000'}
                  fill={inputPath.fillColor}
                  strokeWidth={
                    !!inputPath.strokeWidth ? inputPath.strokeWidth : 1
                  }
                  dash={inputPath.dashed ? [DASHED_DASH, DASHED_GAP] : []}
                  tension={0.5}
                  lineCap="butt"
                  lineJoin="miter"
                  x={inputPath.xOffset || 0}
                  y={inputPath.yOffset || 0}
                  scaleX={!!inputPath.scaleX ? inputPath.scaleX : 6}
                  scaleY={!!inputPath.scaleY ? inputPath.scaleY : 6}
                  visible={inputPath.hidden ? !inputPath.hidden : true}
                />
              ))}
            </Layer>
          )}

          {animations?.length > 0 && (
            <Layer>
              {animations.map((animation, i) => (
                <Animation
                  key={i}
                  ref={animation.ref}
                  imagePath={animation.imagePath}
                  pathPoints={animation.animationPoints}
                  loop={animation.loop}
                />
              ))}
            </Layer>
          )}

          <Layer ref={linesLayerRef} role="application">
            {includeBgOnLines && (
              <Rect
                x={0}
                y={0}
                width={resolvedWidth}
                height={resolvedHeight}
                fill="white"
              />
            )}

            {lines.map((line, i) => (
              <Line
                key={i}
                points={line.points}
                stroke={line.color || '#000'}
                strokeWidth={10}
                tension={0.5}
                lineCap="round"
                lineJoin="round"
                globalCompositeOperation={
                  line.tool === 'eraser' ? 'destination-out' : 'source-over'
                }
              />
            ))}
          </Layer>
        </Stage>
      </div>
    );
  },
);

CanvasDraw.displayName = 'CanvasDraw';
